import React, { useMemo } from 'react';
import { AlertTriangle, Check, LayoutDashboard, Users } from 'lucide-react';
import { themeTokenValues, brandForegroundContrast } from '../../utils/applyBrandTokens';

/**
 * Live preview for Settings → Appearance.
 *
 * Until Phase 7.1 the picker wrote a value the app never read (audit F7), so
 * there was nothing to preview. Now that `applyBrandTokens` exists, the preview
 * is built out of exactly the same function the live app uses — which is the
 * only way a preview stays true: it is not a drawing of the result, it IS the
 * result, scoped to one element.
 *
 * The token overrides go on this element's `style`, so they cascade to the
 * children and nowhere else. Every child then reads `hsl(var(--primary))` the
 * ordinary way, which is why no child needs to know it is in a preview.
 *
 * Both themes are shown side by side on purpose. The derived values differ
 * between them (the dark lightness lift, the inverted soft pair), and an
 * institute picking a near-black navy needs to see what dark mode does to it
 * before saving, not after.
 *
 * ⚠️ An unset colour is simply absent from `themeTokenValues`, so the pane shows
 * the PALETTE's value for it — which is exactly what the app will do. That is
 * the point of previewing the whole theme rather than one picker at a time.
 */

const THEMES = [
  { key: 'light', label: 'Light' },
  { key: 'dark', label: 'Dark' },
];

// The four status families, in the order they read as a scale.
const STATUS_SWATCHES = [
  { key: 'success', label: 'Success' },
  { key: 'info', label: 'Info' },
  { key: 'warning', label: 'Warning' },
  { key: 'danger', label: 'Danger' },
];

function PreviewPane({ appTheme, theme }) {
  const vars = useMemo(() => themeTokenValues(appTheme, theme), [appTheme, theme]);

  // `data-theme` is set on the pane so any child reading a themed token — the
  // surface and text colours below are NOT brand-derived — resolves against the
  // right side of the palette, not the page's current theme.
  return (
    <div
      data-theme={theme}
      style={vars}
      className="tw-flex-1 tw-min-w-[210px] tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface tw-p-3"
    >
      <div className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
        {theme}
      </div>

      <div className="tw-rounded-md tw-bg-surface-raised tw-p-2.5">
        {/* Sidebar chrome — the active row is the surface most people read as
            "the brand colour" before they ever click a button. */}
        <div className="tw-mb-2 tw-space-y-1">
          <div
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-py-1.5 tw-text-[13px] tw-font-medium"
            style={{ background: 'var(--sidebar-active-bg)', color: 'var(--sidebar-active-fg)' }}
          >
            <LayoutDashboard size={14} /> Dashboard
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-py-1.5 tw-text-[13px] tw-text-content-muted">
            <Users size={14} /> Students
          </div>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-rounded-md tw-bg-primary-solid tw-px-2.5 tw-py-1.5 tw-text-[13px] tw-font-semibold tw-text-primary-solid-fg">
            Save changes
          </span>
          <span className="tw-rounded-md tw-border tw-border-solid tw-border-primary tw-px-2.5 tw-py-1.5 tw-text-[13px] tw-font-semibold tw-text-primary">
            Cancel
          </span>
          <span className="tw-rounded-full tw-bg-primary-soft tw-px-2.5 tw-py-1 tw-text-[12px] tw-font-medium tw-text-primary-soft-fg">
            Active
          </span>
        </div>

        {/* A focus ring, because --ring follows the brand too and is the one
            brand surface nobody thinks to check. */}
        <div className="tw-mt-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-2.5 tw-py-1.5 tw-text-[13px] tw-text-content-subtle tw-ring-2 tw-ring-primary/40">
          Search students…
        </div>

        {/* The four status families, as the app actually renders them: the
            `-soft` tint with the `-fg` ink that is readable on it. */}
        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1.5">
          <span className="tw-rounded-full tw-bg-success-soft tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-success-fg">
            Paid
          </span>
          <span className="tw-rounded-full tw-bg-info-soft tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-info-fg">
            Due
          </span>
          <span className="tw-rounded-full tw-bg-warning-soft tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-warning-fg">
            Pending
          </span>
          <span className="tw-rounded-full tw-bg-danger-soft tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-danger-fg">
            Overdue
          </span>
        </div>
      </div>
    </div>
  );
}

export default function BrandPreview({ appTheme }) {
  const primary = appTheme?.mode === 'custom' ? appTheme.primary : '';
  const contrast = useMemo(
    () => (primary ? brandForegroundContrast(primary, 'light') : null),
    [primary]
  );

  return (
    <div>
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {THEMES.map((t) => (
          <PreviewPane key={t.key} appTheme={appTheme} theme={t.key} />
        ))}
      </div>

      {/*
        The honest part, and only for the brand — button text is picked
        automatically (white or near-black, whichever reads better), so most
        colours are fine. What automation cannot fix is a mid-tone where NEITHER
        ink clears 4.5:1; that is worth saying at pick time rather than
        discovering on a support call. The status inks are derived at a fixed
        lightness against their own tint, so they cannot land in that trap.
      */}
      {contrast && (
        <div
          className={`tw-mt-2 tw-flex tw-items-start tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-[12px] ${
            contrast.passesAA
              ? 'tw-bg-success-soft tw-text-success-fg'
              : 'tw-bg-warning-soft tw-text-warning-fg'
          }`}
        >
          {contrast.passesAA ? (
            <Check size={14} className="tw-mt-px tw-shrink-0" />
          ) : (
            <AlertTriangle size={14} className="tw-mt-px tw-shrink-0" />
          )}
          <span>
            Button text will be <strong>{contrast.ink}</strong> at{' '}
            <strong>{contrast.ratio.toFixed(1)}:1</strong> contrast
            {contrast.passesAA
              ? ' — meets WCAG AA.'
              : ' — below the 4.5:1 AA minimum. A slightly darker or lighter shade will fix it.'}
          </span>
        </div>
      )}

      <p className="tw-mb-0 tw-mt-2 tw-text-[12px] tw-text-content-subtle">
        Anything you leave unset keeps the product default — the preview above already shows it that
        way. These colours apply to the LMS app only; your public website keeps its own theme.
      </p>
    </div>
  );
}

export { STATUS_SWATCHES };
